import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import {
  findUpperRecursive,
  genRecursiveOptions,
  getRecursiveArray,
} from '@/utils';
import { z } from 'zod';
import {
  ExpenditurePurposeOfSettlementStatisticsPresetFormSchema,
  ExpenditurePurposeOfSettlementStatisticsPresetsModel,
  ExpenditurePurposesOfSettlementStatisticsModel,
} from '../models';
import {
  EXPENDITURE_BREAKDOWN_PURPOSES_CODE_LENGTH,
  padZeroToLeft,
} from '../utils';

const ExpenditurePurposeOfSettlementStatisticsModelElement =
  ExpenditurePurposesOfSettlementStatisticsModel.element;

export const useExpenditurePurposesOfSettlementStatistics = <
  T = Array<
    z.infer<typeof ExpenditurePurposeOfSettlementStatisticsModelElement>
  >,
>({
  select,
  enabled,
  params,
}: {
  select?: (
    data: z.infer<typeof ExpenditurePurposesOfSettlementStatisticsModel>,
  ) => T;
  enabled?: boolean;
  params?: { referenceFiscalYear?: string };
} = {}) => {
  return useQuery({
    queryKey: ['expenditurePurposesOfSettlementStatistics', params],
    queryFn: async () => {
      const data = await axiosGet(
        '/api/expenditure-purposes-of-settlement-statistics/',
        params,
      );
      return ExpenditurePurposesOfSettlementStatisticsModel.parse(data);
    },
    select,
    enabled,
  });
};

const _useExpenditurePurposeOfSettlementStatisticsRecursive = (
  id?: string | null,
) => {
  return useExpenditurePurposesOfSettlementStatistics({
    select: (data) =>
      id ? findUpperRecursive(id, data, []).reverse() : undefined,
    enabled: !!id,
  });
};

export const useExpenditurePurposeOfSettlementStatisticsOptions = ({
  showCode,
  params,
  enabled,
}: {
  showCode?: boolean;
  params?: { referenceFiscalYear?: string };
  enabled?: boolean;
} = {}) => {
  const options = useExpenditurePurposesOfSettlementStatistics({
    params,
    enabled,
    select: (data) =>
      genRecursiveOptions(
        showCode
          ? padZeroToLeft(
              getRecursiveArray(
                data.filter((d) => !d.upperId),
                data,
              ),
              EXPENDITURE_BREAKDOWN_PURPOSES_CODE_LENGTH,
            )
          : getRecursiveArray(
              data.filter((d) => !d.upperId),
              data,
            ),
        'id',
        'name',
      ),
  });
  return options;
};

const ExpenditurePurposeOfSettlementStatisticsPresetsModelElement =
  ExpenditurePurposeOfSettlementStatisticsPresetsModel.element;

export const useExpenditurePurposeOfSettlementStatisticsPresets = <
  T = Array<
    z.infer<typeof ExpenditurePurposeOfSettlementStatisticsPresetsModelElement>
  >,
>(
  select?: (
    data: z.infer<typeof ExpenditurePurposeOfSettlementStatisticsPresetsModel>,
  ) => T,
) => {
  return useQuery({
    queryKey: ['expenditurePurposeOfSettlementStatisticsPresets'],
    queryFn: async () => {
      const data = await axiosGet(
        '/api/expenditure-purpose-of-settlement-statistics-presets/',
      );
      return ExpenditurePurposeOfSettlementStatisticsPresetsModel.parse(data);
    },
    select,
  });
};

type FormValues = z.infer<
  typeof ExpenditurePurposeOfSettlementStatisticsPresetFormSchema
>;

export const useCreateExpenditurePurposeOfSettlementStatisticsPreset = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const {
        period,
        expenditurePurposeIds,
        expenditurePurposeOfSettlementStatisticsIds,
        ...parsed
      } =
        ExpenditurePurposeOfSettlementStatisticsPresetFormSchema.parse(values);
      const res = await axiosPost(
        '/api/expenditure-purpose-of-settlement-statistics-presets/',
        {
          ...parsed,
          expenditurePurposeId: expenditurePurposeIds.at(-1),
          expenditurePurposeOfSettlementStatisticsId:
            expenditurePurposeOfSettlementStatisticsIds.at(-1),
          startYear: period?.[0]?.year() ?? null,
          endYear: period?.[1]?.year() ?? null,
        },
      );
      await invalidate(['expenditurePurposeOfSettlementStatisticsPresets']);

      return res;
    },
  });
};

export const useUpdateExpenditurePurposeOfSettlementStatisticsPreset = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const {
        id,
        period,
        expenditurePurposeIds,
        expenditurePurposeOfSettlementStatisticsIds,
        ...parsed
      } = ExpenditurePurposeOfSettlementStatisticsPresetFormSchema.extend({
        id: z.string().uuid(),
      }).parse(values);
      const res = await axiosPatch(
        `/api/expenditure-purpose-of-settlement-statistics-presets/${id}/`,
        {
          ...parsed,
          expenditurePurposeId: expenditurePurposeIds.at(-1),
          expenditurePurposeOfSettlementStatisticsId:
            expenditurePurposeOfSettlementStatisticsIds.at(-1),
          startYear: period?.[0]?.year() ?? null,
          endYear: period?.[1]?.year() ?? null,
        },
      );
      await invalidate(['expenditurePurposeOfSettlementStatisticsPresets']);

      return res;
    },
  });
};

export const useDeleteExpenditurePurposeOfSettlementStatisticsPreset = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(
        `/api/expenditure-purpose-of-settlement-statistics-presets/${id}/`,
      );
      await invalidate(['expenditurePurposeOfSettlementStatisticsPresets']);

      return res;
    },
  });
};
